import React from "react"
import { useTranslations } from "@/hooks/useTranslations"
import { useAuth } from "@/hooks/useAuth"
import LoginForm from "@/components/LoginForm"
import { ModuleNotActiveScreen } from "./ModuleNotActiveScreen"
import { UserRole } from "@/types/reports"
import { OptimGovPageLoader } from "@/components/ui/optimgov-loader"

interface ReportsProtectedRouteProps {
	children: React.ReactNode
	requiredRole?: UserRole
	requiredPermission?: string
}

export const ReportsProtectedRoute: React.FC<ReportsProtectedRouteProps> = ({ children, requiredRole, requiredPermission }) => {
	const t = useTranslations()
	const tLogin = useTranslations("login")
	const { user, reportsUser, hasRole, checkPermission, loading, error } = useAuth()

	if (loading) {
		return <OptimGovPageLoader />
	}

	if (error?.code === "auth/module-not-licensed") {
		return <ModuleNotActiveScreen />
	}

	if (!user) {
		return (
			<div className='min-h-screen flex flex-col items-center justify-center bg-slate-100 px-4'>
				<div className='mb-8 flex items-center gap-3'>
					<img src='/OptimGovLogo.png' alt='OptimGov' width={220} height={50} className='h-16 w-auto' />
				</div>
				<LoginForm />
				<div className='mt-8 text-center'>
					<p className='text-sm text-gray-400'>
						&copy; {new Date().getFullYear()} OptimGov. {tLogin("rights")}
					</p>
				</div>
			</div>
		)
	}

	// Check role requirement
	if (requiredRole && !hasRole(requiredRole)) {
		return (
			<div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900'>{t("auth.accessDenied")}</h2>
					<p className='mt-2 text-gray-600'>{t("auth.insufficientRole", { role: requiredRole })}</p>
					<p className='mt-1 text-sm text-gray-500'>{t("auth.currentRole", { role: reportsUser?.role || "Unknown" })}</p>
				</div>
			</div>
		)
	}

	// Check permission requirement
	if (requiredPermission && !checkPermission(requiredPermission)) {
		return (
			<div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900'>{t("auth.accessDenied")}</h2>
					<p className='mt-2 text-gray-600'>{t("auth.insufficientPermission", { permission: requiredPermission })}</p>
				</div>
			</div>
		)
	}

	return <>{children}</>
}
